import React, { useState, useEffect } from 'react';
import billingService from '../../api/billingService';
import customerService from '../../api/customerService';
import { formatCurrency } from '../../utils/formatters';

const ProductList = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [currentAdminEmail, setCurrentAdminEmail] = useState('');

  const filteredProducts = products.filter(p => 
    p.productName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (p.sku && p.sku.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (p.description && p.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );
  const [formData, setFormData] = useState({
    productName: '',
    sku: '',
    description: '',
    costPrice: 0,
    sellingPrice: 0,
    gstRate: 18,
    hsnSacCode: '',
    isRenewable: true,
    billing_interval: 'monthly',
    status: 'Active',
    supplier: '',
    units: 'Units',
    admin: ''
  });

  useEffect(() => {
    fetchProducts();
    fetchSession();
  }, []);

  const fetchSession = async () => {
    try {
      const sessionRes = await customerService.getSession();
      if (sessionRes?.success) {
        setCurrentAdminEmail(sessionRes.userInfo?.admin || sessionRes.userInfo?.email || '');
      }
    } catch (error) {
      console.error('Error fetching session:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const res = await billingService.getProducts();
      console.log('Products API Response:', res);
      if (res && res.success) {
        setProducts(res.data || []);
      } else {
        console.warn('API returned success: false or invalid structure', res);
        setProducts([]);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ 
      ...formData, 
      [name]: type === 'checkbox' ? checked : (type === 'number' ? parseFloat(value) : value) 
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProduct) {
        await billingService.updateProduct(editingProduct._id, formData);
      } else {
        await billingService.createProduct(formData);
      }
      setShowModal(false);
      resetForm();
      fetchProducts();
    } catch (error) {
      alert(error.message);
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      productName: product.productName,
      sku: product.sku || '',
      description: product.description || '',
      costPrice: product.costPrice || 0,
      sellingPrice: product.sellingPrice || 0,
      gstRate: product.gstRate || 18,
      hsnSacCode: product.hsnSacCode || '',
      isRenewable: product.isRenewable !== undefined ? product.isRenewable : true,
      billing_interval: product.billing_interval || 'monthly',
      status: product.status || 'Active',
      supplier: product.supplier || '',
      units: product.units || 'Units',
      admin: product.admin || currentAdminEmail || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        const result = await billingService.deleteProduct(id);
        if (result.success) fetchProducts();
        else alert(result.message);
      } catch (error) {
        console.error('Error deleting product:', error);
      }
    }
  };

  const resetForm = () => {
    setEditingProduct(null);
    setFormData({
      productName: '',
      sku: '',
      description: '',
      costPrice: 0,
      sellingPrice: 0,
      gstRate: 18,
      hsnSacCode: '',
      isRenewable: true,
      billing_interval: 'monthly',
      status: 'Active',
      supplier: '',
      units: 'Units',
      admin: currentAdminEmail || ''
    });
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="fw-700 mb-1">Products</h4>
          <p className="text-muted small mb-0">Manage all your purchasable items and service plans.</p>
        </div>
        <button 
          className="btn btn-primary shadow-sm px-4"
          onClick={() => { resetForm(); setShowModal(true); }}
        >
          <i className="bi bi-plus-lg me-2"></i> Add Product
        </button>
      </div>

      <div className="card border-0 shadow-sm">
        <div className="card-header border-0 bg-transparent pt-4 px-4">
          <div className="d-flex justify-content-between align-items-center">
            <div className="input-group w-25">
              <span className="input-group-text bg-light border-0"><i className="bi bi-search"></i></span>
              <input 
                type="text" 
                className="form-control bg-light border-0" 
                placeholder="Search products..." 
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>
        <div className="table-responsive mt-3">
          <table className="table table-hover align-middle">
            <thead>
              <tr>
                <th>Product Name</th>
                <th>SKU / HSN</th>
                <th>Billing</th>
                <th>Selling Price</th>
                <th>Status</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="6" className="text-center py-5">Loading...</td></tr>
              ) : filteredProducts.length === 0 ? (
                <tr><td colSpan="6" className="text-center py-5">No products found.</td></tr>
              ) : (
                filteredProducts.map((product) => (
                  <tr key={product._id}>
                    <td>
                      <div className="fw-600">{product.productName}</div>
                      <div className="text-xs text-muted text-truncate" style={{maxWidth: '200px'}}>{product.description || 'No description'}</div>
                    </td>
                    <td>
                      <div className="text-xs fw-600 text-primary">{product.sku || '-'}</div>
                      <div className="text-xs text-muted">HSN: {product.hsnSacCode || '-'}</div>
                    </td>
                    <td>
                      <div className="text-xs fw-600 text-capitalize">{product.billing_interval}</div>
                      <div className="text-xs text-muted">{product.isRenewable ? 'Renewable' : 'One-time'}</div>
                    </td>
                    <td>
                      <div className="fw-600">{formatCurrency(product.sellingPrice)}</div>
                      <div className="text-xs text-muted">GST: {product.gstRate}%</div>
                    </td>
                    <td>
                      <span className={`badge badge-soft-${product.status === 'Active' ? 'success' : 'danger'}`}>
                        {product.status}
                      </span>
                    </td>
                    <td className="text-end">
                      <div className="d-flex justify-content-end gap-2">
                        <button className="btn btn-sm btn-light" onClick={() => handleEdit(product)}><i className="bi bi-pencil"></i></button>
                        <button className="btn btn-sm btn-light text-danger" onClick={() => handleDelete(product._id)}><i className="bi bi-trash"></i></button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Product Modal */}
      {showModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content border-0">
              <div className="modal-header border-0">
                <h5 className="modal-title fw-bold">{editingProduct ? 'Edit Product' : 'Add New Product'}</h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body p-4">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label text-muted small fw-600">Product Name</label>
                      <input type="text" name="productName" className="form-control" value={formData.productName} onChange={handleInputChange} required />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label text-muted small fw-600">SKU</label>
                      <input type="text" name="sku" className="form-control" value={formData.sku} onChange={handleInputChange} />
                    </div>
                    <div className="col-12">
                      <label className="form-label text-muted small fw-600">Description</label>
                      <textarea name="description" className="form-control" rows="2" value={formData.description} onChange={handleInputChange}></textarea>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label text-muted small fw-600">Cost Price</label>
                      <input type="number" name="costPrice" className="form-control" value={formData.costPrice} onChange={handleInputChange} required />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label text-muted small fw-600">Selling Price</label>
                      <input type="number" name="sellingPrice" className="form-control" value={formData.sellingPrice} onChange={handleInputChange} required />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label text-muted small fw-600">GST Rate (%)</label>
                      <input type="number" name="gstRate" className="form-control" value={formData.gstRate} onChange={handleInputChange} />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label text-muted small fw-600">HSN/SAC Code</label>
                      <input type="text" name="hsnSacCode" className="form-control" value={formData.hsnSacCode} onChange={handleInputChange} />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label text-muted small fw-600">Billing Interval</label>
                      <select name="billing_interval" className="form-select" value={formData.billing_interval} onChange={handleInputChange}>
                        <option value="monthly">Monthly</option>
                        <option value="quarterly">Quarterly</option>
                        <option value="semi_annual">Semi-Annual</option>
                        <option value="annual">Annual</option>
                        <option value="one_time">One-time</option>
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label text-muted small fw-600">Status</label>
                      <select name="status" className="form-select" value={formData.status} onChange={handleInputChange}>
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label text-muted small fw-600">Supplier</label>
                      <input type="text" name="supplier" className="form-control" value={formData.supplier} onChange={handleInputChange} />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label text-muted small fw-600">Units</label>
                      <input type="text" name="units" className="form-control" value={formData.units} onChange={handleInputChange} />
                    </div>
                    <div className="col-md-3 d-flex align-items-end mb-2">
                      <div className="form-check">
                        <input className="form-check-input" type="checkbox" name="isRenewable" checked={formData.isRenewable} onChange={handleInputChange} id="isRenewableCheck" />
                        <label className="form-check-label text-muted small fw-600" htmlFor="isRenewableCheck">
                          Is Renewable
                        </label>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label text-muted small fw-600">Admin Email</label>
                      <input type="email" name="admin" className="form-control" value={formData.admin} onChange={handleInputChange} required />
                    </div>
                  </div>
                </div>
                <div className="modal-footer border-0 p-4">
                  <button type="button" className="btn btn-light px-4" onClick={() => setShowModal(false)}>Cancel</button>
                  <button type="submit" className="btn btn-primary px-4">{editingProduct ? 'Update Product' : 'Save Product'}</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductList;
